import { checkPhoneNumberValid } from '@ukpostbox/client-data';
import {
	ButtonSubmit,
	ColourStateSolid,
	ContentLoader,
	CountryCodes,
	FlexAlign,
	FlexGap,
	FlexJustify,
	StackHorizontal,
	StackVertical,
	ValidationPatterns,
} from '@ukpostbox/frontend-framework';
import { useFieldArray, useFormContext } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleHistory } from '@fortawesome/pro-solid-svg-icons/faRectangleHistory';
import AddressImporterRow from './AddressImporterRow';

type AddressImporterResultsType = {
	addressData: any;
	embeddedForm?: boolean;
	removeCallback?: any;
};

function AddressImporterResults(props: AddressImporterResultsType) {
	const [addressData, setAddressData] = useState<any>(null);
	const { control, formState, setError } = useFormContext();

	const [submitBlocker, setSubmitBlocker] = useState<boolean>(false);

	const { fields, remove, append } = useFieldArray({
		control,
		name: `Addresses`,
		shouldUnregister: true,
	});

	const HandleRemove = (id: number) => {
		remove(id);
		if (fields.length === 1) {
			setAddressData(null);
			props.removeCallback(fields.length);
		}
	};

	const CountryCodeFixup = (code: string, name: string) => {
		let countryCode = code.toLowerCase().replace(/'/g, '');
		const codeName = name.toLowerCase().replace(/'/g, '');

		if (countryCode === 'uk') {
			countryCode = 'GB';
		}

		if (
			codeName === 'united kingdom' ||
			codeName === 'england' ||
			codeName === 'scotland' ||
			codeName === 'wales' ||
			codeName === 'northern ireland'
		) {
			countryCode = 'GB';
		}

		return countryCode.toUpperCase();
	};

	const RowDataValid = async (data: any) => {
		const emailValid = ValidationPatterns.email.test(data.emailAddress);
		const phoneValid =
			ValidationPatterns.telephone.test(data.phoneNo) &&
			(await checkPhoneNumberValid(data.phoneNo.replace('+', '%2B')).then((r) => r.isValid));

		return (
			data.name !== '' &&
			data.propertyNameNumber !== '' &&
			data.addressLine1 !== '' &&
			data.addressLine2 !== '' &&
			data.addressLine3 !== '' &&
			data.postCode !== '' &&
			data.countryCode !== '' &&
			emailValid &&
			phoneValid
		);
	};

	const SortAddressData = async () => {
		const invalidRows: Array<any> = [];
		const validRows: Array<any> = [];

		await Promise.all(
			addressData?.map((field: any, fieldIndex: number) => {
				const phoneNo = field.phoneNo
					.replace(/ /g, '')
					.replace(/-/g, '')
					.replace(/\(/g, '')
					.replace(/\)/g, '')
					.trim();
				const countryEntry = field.countryCode
					? CountryCodes[
							GetArrayIndexByKeyValue(
								CountryCodes,
								'value',
								CountryCodeFixup(field.countryCode, field.countryName),
							)
						]
					: CountryCodes[GetArrayIndexByKeyValue(CountryCodes, 'label', field.countryName)];
				const newAddressData = {
					...field,
					phoneNo,
					countryCode: countryEntry?.value,
					countryName: countryEntry?.label,
				};

				return RowDataValid(newAddressData).then((valid: boolean) => {
					if (valid) {
						validRows.push({ ...newAddressData, validRow: true });
					} else {
						invalidRows.push({ ...newAddressData, validRow: false });
					}
					return true;
				});
			}),
		).then(() => {
			[...invalidRows, ...validRows].forEach((item: any, index: number) => {
				append({ ...item });
			});
		});
	};

	useEffect(() => {
		if (addressData) {
			remove();
			SortAddressData().then(() => null);
		}
	}, [addressData]);

	useEffect(() => {
		if (props.addressData) {
			setAddressData(props.addressData);
		}
	}, [props]);

	return (
		<>
			{fields.map((field: any, fieldIndex: number) => (
				<AddressImporterRow
					{...field}
					key={field.id}
					fieldIndex={fieldIndex}
					removeCallback={(id: number) => HandleRemove(id)}
					embedded={props.embeddedForm}
					lookUpsActive={(e) => setSubmitBlocker(e)}
				/>
			))}

			{fields.length > 0 && (
				<div className={`sticky sticky-bottom p-md secondary mt-sm ${props.embeddedForm ? 'round' : ''}`}>
					<StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between}>
						<div>{fields.length} Addresses to Import</div>
						<ButtonSubmit
							title='Import'
							colour={ColourStateSolid.Primary}
							disabled={Object.keys(formState.errors).length !== 0 || submitBlocker}
						/>
					</StackHorizontal>
				</div>
			)}

			{!addressData && (
				<StackVertical
					gap={FlexGap.ExtraSmall}
					align={FlexAlign.Center}
					justify={FlexJustify.Center}
					extraClass='m-xxl p-xxl text-center'
				>
					<FontAwesomeIcon icon={faRectangleHistory} size='3x' />
					<h6 className='sub-heading mt-sm mb-xs'>No Data Loaded</h6>
				</StackVertical>
			)}

			{addressData && fields.length === 0 && (
				<StackVertical
					gap={FlexGap.ExtraSmall}
					align={FlexAlign.Center}
					justify={FlexJustify.Center}
					extraClass='m-xxl p-xxl text-center'
				>
					<ContentLoader />
				</StackVertical>
			)}
		</>
	);
}

export default AddressImporterResults;
